import { randomUuid } from '@lemonade-hq/ts-helpers';
import { clsx } from 'clsx';
import type { RefObject } from 'react';
import { forwardRef, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';
import type { ResponsiveValues } from '../../config/responsiveness';
import { spacing } from '../../styles/spacing.css';
import type { textTransformVariants } from '../BaseComponents.css';
import { Flex } from '../Flex/Flex';
import { Text } from '../Text/Text';
import * as styles from './Signature.css';

type TextAlign = 'center' | 'end' | 'start';

export interface SignatureProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'placeholder'> {
  readonly underlineLabel?: string;
  readonly textTransform?: keyof typeof textTransformVariants;
  readonly textAlign?: ResponsiveValues<TextAlign> | TextAlign;
}

const getRefId = (idRef: RefObject<string>): string => {
  return `signature_${idRef.current}`;
};

export const Signature = forwardRef<HTMLInputElement, SignatureProps>(
  ({ className, underlineLabel, disabled, textTransform, textAlign, ...props }, ref) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const idRef = useRef(randomUuid());

    return (
      <Flex
        flexDirection="column"
        gap={spacing.s08}
        position="relative"
        textAlign={
          textAlign ?? {
            sm: 'center',
            md: 'left',
          }
        }
      >
        <input
          aria-label={underlineLabel}
          className={clsx(styles.signature({ textTransform }), className)}
          disabled={disabled}
          id={getRefId(idRef)}
          ref={mergeRefs([internalRef, ref])}
          {...props}
        />
        <Text as="label" className={styles.description} htmlFor={getRefId(idRef)} type="bodyTiny">
          {underlineLabel}
        </Text>
      </Flex>
    );
  },
);
